import { Box, Button, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import React, { useContext, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import ModalContext, { ConfirmationServiceContext } from '../../context/modal';
import { WhiteCheckbox, WhiteInput, WhiteLink } from './AppModals';
import FirebaseContext from '../../context/firebase';
import firebase from 'firebase/app';
import useValidators from '../../hooks/useValidators';
import SnackBarContext from '../../context/snackbar';
import useAccountsApiRoutes from '../../hooks/api/useAccountsApiRoutes';

type Props = {
  // onClose: (
  //   event: {},
  //   reason: 'backdropClick' | 'escapeKeyDown' | 'closeButtonClick'
  // ) => void;
};
interface MyFormValues {
  email: string;
  password: string;
  keepMeLoggedIn: boolean;
}

// Firebase's messages ("There is no user record corresponding to this
// identifier…") are not for end users. Map the codes we expect to plain
// wording; anything else falls back to a generic line.
const friendlyLoginError = (error: any) => {
  switch (error?.code) {
    case 'auth/user-not-found':
      return "We couldn't find an account for that email address. Check the spelling, or create a new account.";
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return 'That email and password don’t match. Try again, or use “Forgot my password”.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact us if you think this is a mistake.';
    case 'auth/too-many-requests':
      return 'Too many attempts — please wait a few minutes and try again, or reset your password.';
    case 'auth/network-request-failed':
      return 'Couldn’t reach the server. Check your connection and try again.';
    default:
      return error?.response?.data?.message?.replace(/^Error:\s*/, '') || 'Login failed. Please try again.';
  }
};

const LoginModal = (props: Props) => {
  // const { onClose } = props;
  const classes = useStyles();
  const { passwordReset } = useAccountsApiRoutes();
  const { setDrawerOpen } = useContext(ModalContext);
  const { firebaseApp } = useContext(FirebaseContext);
  const { showSnack } = useContext(SnackBarContext);
  const { showConfirmationModal, setOpenId } = useContext(ConfirmationServiceContext);
  const { validateEmail } = useValidators();
  // Shown inside the form (not a toast) so it sits next to the fields it's about.
  const [loginError, setLoginError] = useState('');

  const initialValues: MyFormValues = {
    email: '',
    password: '',
    keepMeLoggedIn: false,
  };
  const login = (values: MyFormValues, { setSubmitting }: FormikHelpers<MyFormValues>) => {
    setLoginError('');
    if (firebaseApp) {
      const persistence = values.keepMeLoggedIn
        ? firebase.auth.Auth.Persistence.LOCAL
        : firebase.auth.Auth.Persistence.SESSION;
      firebaseApp
        .auth()
        .setPersistence(persistence)
        .then(() => firebaseApp.auth().signInWithEmailAndPassword(values.email, values.password))
        .then(() => {
          setSubmitting(false);
          setDrawerOpen(undefined);
        })
        .catch((error) => {
          setSubmitting(false);
          setLoginError(friendlyLoginError(error));
        });
    }
  };

  return (
    <Box flex={1} display="flex" flexDirection="column">
      <Typography variant="h4">Login</Typography>
      <Formik initialValues={initialValues} validate={validateEmail} onSubmit={login}>
        {({ values, handleChange, handleSubmit, isSubmitting, isValid }) => {
          const handleForgotPassword = async () => {
            setLoginError('');
            if (!values.email) {
              setLoginError('Enter your email address above first, and we’ll send a reset link to it.');
              return;
            }
            const res = await showConfirmationModal({
              title: 'Forgot your Password',
              contentText: `Send forgot password link to ${values.email}?`,
            });
            if (res && res.confirmed === true) {
              try {
                await passwordReset(values.email);
                setOpenId(undefined);
                showSnack('Please check your email for further instructions', 'success');
              } catch (error) {
                setOpenId(undefined);
                setLoginError(friendlyLoginError(error));
              }
            }
          };
          return (
            <form onSubmit={handleSubmit} className={classes.form}>
              <WhiteInput label="Email" type="email" name="email" />
              <WhiteInput label="Password" type="password" name="password" />
              {loginError ? (
                <Box mt={2} px={2} py={1.5} bgcolor="rgba(255,255,255,0.92)" borderRadius="8px" role="alert">
                  <Typography variant="body2" color="error" fontWeight={600}>
                    {loginError}
                  </Typography>
                </Box>
              ) : null}
              <WhiteCheckbox
                label="Keep me logged in"
                checked={values.keepMeLoggedIn}
                onChange={handleChange}
                name="keepMeLoggedIn"
              />
              <Box display="flex" marginTop={3}>
                <Button
                  variant="contained"
                  size="large"
                  classes={{ root: classes.loginButtonRoot }}
                  type="submit"
                  disabled={!isValid || isSubmitting}
                >
                  Login
                </Button>
                <Button
                  classes={{ root: classes.createButtonRoot }}
                  variant="outlined"
                  size="large"
                  onClick={() => setDrawerOpen('signup')}
                >
                  Create New Account
                </Button>
              </Box>

              <Box display="flex" flex={1} alignItems="flex-end" justifyContent="center">
                <WhiteLink onClick={handleForgotPassword}>Forgot my password</WhiteLink>
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default LoginModal;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginButtonRoot: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.info.main,
      marginRight: theme.spacing(2.25),
    },
    createButtonRoot: {
      backgroundColor: 'unset',
      color: theme.palette.common.white,
      borderColor: theme.palette.common.white,
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },
  })
);
